<template>
    <div
        class="SidebarMenuGroup"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <NewCustomDropdown
            ref="dropdown"
            :popover-class="popoverClass"
            popover-teleported
            portal-target="PopoverTarget"
            position-x="start"
            position-y="bottom"
            auto-flip="y"
            :offset-y="dropdownOffsetY"
            :offset-x="dropdownOffsetX"
            :items="dropdownItems"
            :close-on-select="true"
            :enable-search="false"
            @select="onSelectItem"
            @open="onDropdownOpen"
            @close="onDropdownClose"
        >
            <template #trigger>
                <SidebarMenuItem
                    class="SidebarMenu__topLevelItem"
                    :icon="icon"
                    :active="active"
                    :label="label"
                />
            </template>
            <template #item="{ item }">
                <span
                    class="SidebarMenuGroup__item"
                    :class="{
                        'SidebarMenuGroup__item--active': isItemActive(item.value)
                    }"
                >
                    {{ item.label }}
                </span>
            </template>
        </NewCustomDropdown>
    </div>
</template>

<style lang="scss">
.SidebarMenuGroup {
    position: relative;

    &__item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 400;
        color: var(--eidQualityBlack);

        &--active {
            font-weight: 500;
        }
    }
}
</style>
<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import NewCustomDropdown from '@evidentid/dashboard-commons/components/Dropdown/NewCustomDropdown.vue';

    import SidebarMenuItem from './SidebarMenuItem.vue';

    interface MenuItem {
        label: string;
        value: string;
        href: string;
    }

    export default defineComponent({
        name: 'SidebarMenuGroup',
        components: {
            NewCustomDropdown,
            SidebarMenuItem,
        },
        props: {
            icon: {
                type: [ String, Object ],
                required: true,
            },
            label: {
                type: String,
                required: true,
            },
            items: {
                type: Array as PropType<MenuItem[]>,
                required: true,
            },
            active: {
                type: Boolean,
                default: false,
            },
            activeItemValue: {
                type: String,
                default: null,
            },
            popoverClass: {
                type: String,
                required: true,
            },
        },
        emits: [ 'dropdown-opening' ],
        data() {
            return {
                hideTimeout: null as number | null,
                isDropdownOpen: false,
                // Dynamically calculated menu item dimensions
                menuItemWidth: 0,
                menuItemHeight: 0,
            };
        },
        computed: {
            dropdownItems(): MenuItem[] {
                return this.items;
            },
            /**
             * Vertical offset to position dropdown at menu item top edge
             * Negative value moves dropdown up by (menu item height + original gap margin 5px)
             */
            dropdownOffsetY(): number {
                return -(this.menuItemHeight + 5);
            },
            /**
             * Horizontal offset to position dropdown to the right of menu item
             * Positive value moves dropdown right by menu item width
             */
            dropdownOffsetX(): number {
                return this.menuItemWidth + 4;
            },
        },
        mounted() {
            this.calculateMenuItemDimensions();
        },
        beforeUnmount() {
            this.cancelHideTimeout();
        },
        methods: {
            calculateMenuItemDimensions() {
                this.$nextTick(() => {
                    const dropdown = this.$refs.dropdown as any;
                    const menuItemElement = dropdown?.$el?.querySelector('.SidebarMenuItem__link');
                    if (menuItemElement) {
                        const rect = menuItemElement.getBoundingClientRect();
                        this.menuItemWidth = rect.width;
                        this.menuItemHeight = rect.height;
                    }
                });
            },
            onMouseEnter() {
                this.cancelHideTimeout();
                const dropdown = this.$refs.dropdown as any;
                if (dropdown && !dropdown.isPopoverOpen) {
                    dropdown.openPopover();
                }
            },
            onMouseLeave() {
                this.scheduleHide();
            },
            cancelHideTimeout() {
                if (this.hideTimeout) {
                    clearTimeout(this.hideTimeout);
                    this.hideTimeout = null;
                }
            },
            scheduleHide() {
                this.cancelHideTimeout();
                this.hideTimeout = window.setTimeout(() => {
                    const dropdown = this.$refs.dropdown as any;
                    if (dropdown) {
                        dropdown.closePopover();
                    }
                }, 200);
            },
            onDropdownOpen() {
                this.isDropdownOpen = true;
                this.$emit('dropdown-opening');
                this.$nextTick(() => {
                    this.addPopoverHoverListeners();
                });
            },
            onDropdownClose() {
                this.isDropdownOpen = false;
                this.removePopoverHoverListeners();
            },
            addPopoverHoverListeners() {
                // Handle multiple classes by converting spaces to dots for querySelector
                const selector = this.popoverClass.split(' ').map((cls) => `.${cls}`).join('');
                const popover = document.querySelector(selector) as HTMLElement | null;
                if (popover) {
                    popover.addEventListener('mouseenter', this.cancelHideTimeout);
                    popover.addEventListener('mouseleave', this.scheduleHide);
                }
            },
            removePopoverHoverListeners() {
                // Handle multiple classes by converting spaces to dots for querySelector
                const selector = this.popoverClass.split(' ').map((cls) => `.${cls}`).join('');
                const popover = document.querySelector(selector) as HTMLElement | null;
                if (popover) {
                    popover.removeEventListener('mouseenter', this.cancelHideTimeout);
                    popover.removeEventListener('mouseleave', this.scheduleHide);
                }
            },
            onSelectItem(item: MenuItem) {
                window.location.href = item.href;
            },
            closeDropdown() {
                this.cancelHideTimeout();
                const dropdown = this.$refs.dropdown as any;
                if (dropdown) {
                    dropdown.closePopover();
                }
            },
            isItemActive(value: string): boolean {
                return this.activeItemValue === value;
            },
        },
    });
</script>
